<script setup>
import { watch, ref, onMounted, onBeforeUnmount } from 'vue'
import { ArrowsPointingInIcon } from '@heroicons/vue/24/outline'

import PostMenu from '@/Pages/Post/Partials/PostMenu.vue'
import { useStorage } from '@vueuse/core'
import { usePostStore } from '@/Stores/PostStore'
import PostVideo from '@/Pages/Post/Partials/PostVideo.vue'
import PostControls from '@/Pages/Post/Partials/PostControls.vue'
import PostContent from '@/Pages/Post/Partials/PostContent.vue'
import PostImage from '@/Pages/Post/Partials/PostImage.vue'
import { useAnalyticsStore } from '@/Stores/AnalyticsStore'
import PreloadManager from '@/Services/VideoPlayer/PreloadManager'

const props = defineProps({
	post: { type: Object, required: true },
	index: { type: Number },
	isNext: { type: Boolean, default: false },
	isPrev: { type: Boolean, default: false },
});

const analyticsStore = useAnalyticsStore();
const postStore = usePostStore();
const emit = defineEmits(['enter', 'active', 'leave', 'videoEnded', 'ad-playing']);
const postElement = ref(null);
const isActive = ref(false);
const hasEntered = ref(false);
const isAdPlaying = ref(false);
const isFullscreen = ref(false);
let observer = null;

const toggleFullscreen = () => {
	isFullscreen.value = !isFullscreen.value;
};

// Preload next/prev videos
watch(() => props.isNext, (next) => {
	if (next && props.post.media?.fmp4?.url) {
		PreloadManager.preloadStream(props.post.media.fmp4.url, 'full');
	}
});

// IntersectionObserver for detecting active post
onMounted(() => {
	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const ratio = entry.intersectionRatio;

				// Entering viewport (70%)
				if (ratio > 0.7 && !hasEntered.value) {
					hasEntered.value = true;
					emit('enter');
				}

				// Fully active (90%)
				if (ratio > 0.9 && !isActive.value) {
					isActive.value = true;
					emit('active');

					// Trigger side effects when post becomes active
					postStore.setCurrentPost(props.post);
					triggerEvents();
					saveToHistory();
					isFullscreen.value = false;
				}

				// Leaving viewport (< 30%)
				if (ratio < 0.3 && (hasEntered.value || isActive.value)) {
					hasEntered.value = false;
					isActive.value = false;
					emit('leave');
				}
			});
		},
		{
			threshold: [0.3, 0.7, 0.9],
			rootMargin: '0px'
		}
	);

	if (postElement.value) {
		observer.observe(postElement.value);
	}
});

onBeforeUnmount(() => {
	if (observer) {
		observer.disconnect();
		observer = null;
	}
});

/**
 * Save post to history collection
 */
const saveToHistory = () => {
	let posts = useStorage('history', []).value || [];

	if (!posts.includes(props.post.uuid)) {
		// Add new post to the end
		posts.push(props.post.uuid);

		// Limit history to 100 most recent posts
		if (posts.length > 100) {
			posts = posts.slice(-100); // Keep only the last 100 posts
		}

		useStorage('history', posts);
	}
}

/**
 *
 */
const triggerEvents = () => {

	let post_path = props.post.url.replace(/^https?:\/\/[^/]+/, '')

	analyticsStore.sendAnalyticsEvent('post_view', {
		post_id: props.post.uuid,
		post_path: post_path,
		post_title: props.post.title
	});
}

const adPlayingStatus = (status) => {
    isAdPlaying.value = status;
    emit('ad-playing', status);
}
</script>

<template>
	<div ref="postElement" class="relative w-full h-full flex-1 rounded-lg">

		<div v-if="!isAdPlaying && !isFullscreen" class="absolute bottom-0 left-0 right-0 z-20 pointer-events-auto">
			<div class="flex space-x-6 items-end md:p-8 p-4 w-full min-h-[200px] bg-gradient-to-t from-black/70 via-black/30 to-transparent">
				<PostContent/>
				<PostControls :is-fullscreen="isFullscreen" @toggle-fullscreen="toggleFullscreen"/>
			</div>
		</div>

		<!-- Fullscreen exit button -->
		<div v-if="!isAdPlaying && isFullscreen" class="absolute bottom-4 right-4 z-20 pointer-events-auto">
			<div class="text-white justify-center flex">
				<button 
					@click="toggleFullscreen" 
					class="outline-none"
					type="button"
					aria-label="Exit Fullscreen"
				>
					<ArrowsPointingInIcon class="h-6 md:h-8"/>
				</button>
			</div>
		</div>

		<PostVideo
		 v-if="post.type === 'video'"
		 :post="post"
		 :poster="post.media_poster"
		  :source="post.media"
		  :is-active="isActive"
		@ended="emit('videoEnded')"
        @ad-playing="adPlayingStatus"
        />

		<PostImage v-else/>

		<PostMenu v-if="!isAdPlaying && !isFullscreen"/>
	</div>
</template>

